import { formatTime } from '@/lib/dates';

/**
 * The game card's accessible name is identity only — who, where, when, and a
 * bare count of what is wrong. Every warning and error sentence lives in the
 * description instead, so a screen reader announces the name first and the
 * detail after it, where it can be cut off. An unwarned card gets no
 * description element at all, never an empty one.
 */
export type GameCardAria = {
  /** The accessible name: "Demo 05 versus Demo 04, Court 1, 5:15pm. 1 warning." */
  label: string;
  /** The sentences for the aria-describedby element, errors first. */
  description: string[];
  /** The element id to reference — null when there is nothing to describe. */
  descriptionId: string | null;
};

export function buildGameCardAria(
  g: {
    id?: string | number;
    team_a_name: any;
    team_b_name: any;
    court_id?: string | null;
    scheduled_start_time?: string | null;
  },
  courtLabel: string | null,
  issueDetails: { errors?: string[]; warnings?: string[] } | null,
  warns: string[] = [],
): GameCardAria {
  // The publish check and the drop-time warnings can name the same problem,
  // so the sentences are deduped before they are counted or announced.
  const errors = Array.from(new Set(issueDetails?.errors ?? []));
  const warnings = Array.from(
    new Set([...(issueDetails?.warnings ?? []), ...warns]),
  ).filter((w) => !errors.includes(w));

  const placed = Boolean(g.court_id && g.scheduled_start_time);
  const identity = [
    `${g.team_a_name} versus ${g.team_b_name}`,
    placed
      ? [courtLabel, formatTime(g.scheduled_start_time as string)].filter(Boolean).join(', ')
      : null,
  ]
    .filter(Boolean)
    .join(', ');

  const counts = [
    errors.length ? `${errors.length} ${errors.length === 1 ? 'error' : 'errors'}` : null,
    warnings.length ? `${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}` : null,
  ].filter(Boolean);

  const description = [...errors, ...warnings];
  return {
    label: counts.length ? `${identity}. ${counts.join(', ')}.` : identity,
    description,
    descriptionId: description.length ? `grid-game-desc-${g.id}` : null,
  };
}
